import { Activity, Clock3, RefreshCw } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { apiService } from '../services/api';
import { SessionDefinition } from '../types/api';

type SessionCodeState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  code?: string;
  expiresAtMs?: number;
};

const renderColoredCode = (code: string) => (
  code.toUpperCase().split('').map((char, index) => {
    const isLetter = /[A-Z]/.test(char);
    const isNumber = /[0-9]/.test(char);

    if (isLetter) {
      return (
        <span key={`${char}-${index}`} style={{ color: '#D97706' }}>
          {char}
        </span>
      );
    }
    if (isNumber) {
      return (
        <span key={`${char}-${index}`} style={{ color: '#059669' }}>
          {char}
        </span>
      );
    }
    return (
      <span key={`${char}-${index}`} style={{ color: '#6B7280' }}>
        {char}
      </span>
    );
  })
);

const formatCountdown = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const SessionDefinitionList: React.FC<{
  sessions: SessionDefinition[];
  codePayloadBase?: {
    providerId?: string;
    programId?: string;
    enrollmentId?: string;
    userId?: string;
  };
}> = ({ sessions, codePayloadBase }) => {
  const [codesBySessionId, setCodesBySessionId] = useState<Record<string, SessionCodeState>>({});
  const [nowMs, setNowMs] = useState(() => Date.now());

  const hasActiveCountdown = useMemo(
    () => Object.values(codesBySessionId).some((state) => state.status === 'success' && (state.expiresAtMs || 0) > nowMs),
    [codesBySessionId, nowMs]
  );

  useEffect(() => {
    if (!hasActiveCountdown) return undefined;

    const interval = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [hasActiveCountdown]);

  if (!sessions.length) {
    return <p className="text-sm text-secondary-500">None</p>;
  }

  const generateCode = async (session: SessionDefinition) => {
    setCodesBySessionId((previous) => ({
      ...previous,
      [session.id]: { status: 'loading' },
    }));

    try {
      const response = await apiService.createSessionCode({
        sessionDefinitionId: session.id,
        ...codePayloadBase,
      });

      setCodesBySessionId((previous) => ({
        ...previous,
        [session.id]: {
          status: 'success',
          code: response.sessionCode.toUpperCase(),
          expiresAtMs: Date.now() + (response.expiresIn * 1000),
        },
      }));
      setNowMs(Date.now());
    } catch {
      setCodesBySessionId((previous) => ({
        ...previous,
        [session.id]: { status: 'error' },
      }));
    }
  };

  return (
    <ol className="space-y-3">
      {sessions.map((session) => (
        <li key={session.id} className="inline-block w-full max-w-[600px] p-3 bg-secondary-50 rounded-lg border border-secondary-200">
          <div className="flex items-start justify-between gap-3">
            <p className="font-medium text-secondary-900">{session.name}</p>
            <div className="shrink-0">
              {(() => {
                const state = codesBySessionId[session.id];
                const remainingSeconds = state?.expiresAtMs
                  ? Math.max(0, Math.ceil((state.expiresAtMs - nowMs) / 1000))
                  : 0;

                if (state?.status === 'loading') {
                  return (
                    <button type="button" disabled className="btn-secondary inline-flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Generating...</span>
                    </button>
                  );
                }

                if (state?.status === 'error') {
                  return (
                    <div className="px-3 py-2 rounded-md bg-red-100 text-red-700 text-xs font-semibold">
                      Code Request Failed
                    </div>
                  );
                }

                if (state?.status === 'success' && state.code && remainingSeconds > 0) {
                  return (
                    <div className="px-3 py-2 rounded-md bg-primary-50 border border-primary-400">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono font-bold tracking-wide">
                          {renderColoredCode(state.code)}
                        </code>
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-white border border-secondary-300 text-secondary-700 font-mono">
                          <Clock3 className="h-3 w-3" />
                          {formatCountdown(remainingSeconds)}
                        </span>
                      </div>
                    </div>
                  );
                }

                return (
                  <button
                    type="button"
                    onClick={() => generateCode(session)}
                    className="btn-secondary text-xs bg-primary-600 hover:bg-primary-700 text-white"
                  >
                    Generate Code
                  </button>
                );
              })()}
            </div>
          </div>
          {session.description && (
            <p className="text-sm text-secondary-500 mt-2 px-2 py-1">{session.description}</p>
          )}
        </li>
      ))}
    </ol>
  );
};

export const AvailableSessionsPage: React.FC = () => {
  const { data, isLoading, error, refetch } = useQuery(
    'available-sessions',
    () => apiService.getAvailableSessions(),
    { refetchInterval: 120_000 }
  );

  const enrolled = data?.sessionDefinitions?.enrolled || [];
  const publicSessions = data?.sessionDefinitions?.public || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Available Sessions</h1>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-secondary-100 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <Activity className="h-10 w-10 text-secondary-300 mx-auto mb-3" />
            <p className="text-secondary-600">Failed to load available sessions.</p>
            <button type="button" onClick={() => refetch()} className="btn-primary mt-4">
              Try Again
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {enrolled.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">Enrolled Programs</h2>
                <div className="space-y-4">
                {enrolled.map((group, groupIndex) => (
                    <div key={`${group.providerName}-${group.programName}-${groupIndex}`} className="inline-block border border-secondary-200 rounded-lg p-4">
                    <h3 className="text-base font-semibold text-secondary-900">
                      {group.enrollment.name || group.programName} - {group.providerName}
                    </h3>
                    <div className="mt-3">
                      <SessionDefinitionList
                        sessions={group.sessions || []}
                        codePayloadBase={{
                          providerId: group.enrollment?.providerId,
                          programId: group.enrollment?.programId,
                          userId: group.enrollment?.userId,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
            )}
            {publicSessions.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">Public Sessions</h2>
              <SessionDefinitionList sessions={publicSessions} />
            </section>
            )}
            {enrolled.length === 0 && publicSessions.length === 0 && (
              <div className="text-center py-8">
                <Activity className="h-10 w-10 text-secondary-300 mx-auto mb-3" />
                <p className="text-secondary-600">No available sessions.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
